import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackSelectionModal = ({ 
  isOpen, 
  onClose, 
  onBack,
  onSelectTrack,
  sessionId,
  userToken,
  sourceType // 'favorites' or 'saved'
}) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchTracks();
    }
  }, [isOpen, sourceType]);

  const fetchTracks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (sourceType === 'favorites') {
        response = await axios.get(`${API}/favorites`, {
          headers: { 'Authorization': `Bearer ${userToken}` }
        });
        // Favorites have a track object nested
        setTracks(response.data.map(fav => fav.track));
      } else if (sourceType === 'saved') {
        response = await axios.get(`${API}/identification_history`, {
          headers: { 'Authorization': `Bearer ${userToken}` }
        });
        // Identification history has track_info
        setTracks(response.data.map(item => ({
          id: item.id,
          title: item.track_info?.title || 'Unknown Track',
          artist: item.track_info?.artist || 'Unknown Artist',
          preview_url: item.track_info?.preview_url,
          artwork: item.track_info?.album?.images?.[0]?.url || item.track_info?.artwork,
          duration_ms: item.track_info?.duration_ms || 30000
        })));
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch tracks:', err);
      setError(err.response?.data?.detail || 'Failed to load tracks');
      setLoading(false);
    }
  };

  const handleAddToQueue = async () => {
    if (!selectedTrack) {
      alert('Please select a track first');
      return;
    }

    try {
      await axios.post(
        `${API}/jam/${sessionId}/queue`,
        {
          track_id: selectedTrack.id,
          title: selectedTrack.title,
          artist: selectedTrack.artist,
          preview_url: selectedTrack.preview_url,
          duration_ms: selectedTrack.duration_ms || 30000,
          artwork: selectedTrack.artwork
        },
        { headers: { 'Authorization': `Bearer ${userToken}` } }
      );
      
      onSelectTrack(selectedTrack);
      onClose();
    } catch (err) {
      console.error('Failed to add track to queue:', err);
      alert(err.response?.data?.detail || 'Failed to add track to queue');
    }
  };

  if (!isOpen) return null;

  const title = sourceType === 'favorites' ? '❤️ Your Favorites' : '💾 Your Saved Tracks';
  const emptyMessage = sourceType === 'favorites' 
    ? 'No favorites yet. Add tracks to favorites from Discovery Zone!'
    : 'No saved tracks yet. Identify tracks to build your library!';

  return (
    <div className="jam-modal-overlay" onClick={onClose}>
      <div className="jam-modal-content track-selection-modal" onClick={(e) => e.stopPropagation()}>
        <div className="jam-modal-header">
          <h2>{title}</h2>
          <button className="jam-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="jam-modal-body">
          {loading && (
            <div className="track-selection-loading">
              <div className="loading-spinner"></div>
              <p>Loading tracks...</p>
            </div>
          )}

          {error && (
            <div className="track-selection-error">
              <p>❌ {error}</p>
              <button className="jam-btn jam-btn-secondary jam-btn-small" onClick={fetchTracks}>
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && tracks.length === 0 && (
            <div className="track-selection-empty">
              <p style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎵</p>
              <p>{emptyMessage}</p>
            </div>
          )}

          {!loading && !error && tracks.length > 0 && (
            <div className="track-selection-list">
              {tracks.map((track) => (
                <div 
                  key={track.id}
                  className={`track-selection-item ${selectedTrack?.id === track.id ? 'selected' : ''}`}
                  onClick={() => setSelectedTrack(track)}
                >
                  {track.artwork && (
                    <img 
                      src={track.artwork} 
                      alt={track.title}
                      className="track-selection-artwork"
                    />
                  )}
                  <div className="track-selection-info">
                    <h4>{track.title}</h4>
                    <p>{track.artist}</p>
                  </div>
                  {selectedTrack?.id === track.id && (
                    <span className="track-selection-check">✓</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="jam-modal-footer" style={{ justifyContent: 'space-between' }}>
          {onBack && (
            <button 
              className="jam-btn jam-btn-secondary" 
              onClick={onBack}
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
            >
              ← Back
            </button>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            {tracks.length > 0 && (
              <button 
                className="jam-btn jam-btn-primary"
                onClick={handleAddToQueue}
                disabled={!selectedTrack}
                style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
              >
                Add to Queue
              </button>
            )}
            <button 
              className="jam-btn jam-btn-secondary" 
              onClick={onClose}
              style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrackSelectionModal;
